import config from '../config';
import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import "../style/createCampaign.css";
import CustomValues from "../components/customValues";
import DeviceModal from "../components/DeviceModal";
import CampaignLocation from "../components/CampaignLocation";
import { Tablet, User, Plane, Loader2 } from "lucide-react";
import axios from "axios";
import { useAuth } from "../context/authContext";
import Button from "../components/Button";

const Campaign = () => {
  const { authData } = useAuth();
  const { organizationId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // flag for Inventory Access
  const isInventoryAccessEnabled = config.INVENTORY_ACCESS_ENABLED;

  const [templates, setTemplates] = useState([]);
  const [logoUrl, setLogoUrl] = useState(null);
  const customValuesRef = useRef();
  const [campaignId, setCampaignId] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const { accountId } = location.state || {};
  const [showModal, setShowModal] = useState(false);
  const [modalSearch, setModalSearch] = useState("");
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [activeTab, setActiveTab] = useState("Members");
  const { orgId, orgName } = location.state || "";
  const [accountName, setAccountName] = useState("");
  const [campaignData, setCampaignData] = useState(null);
  const [showPopup, setShowPopup] = useState(false);
  const [popupMessage, setPopupMessage] = useState("");
  const [popupType, setPopupType] = useState("success");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [initialFormData, setInitialFormData] = useState(null);
  const [customValuesChanged, setCustomValuesChanged] = useState(false);
  const [customValuesInitialData, setCustomValuesInitialData] = useState(null);
  const [toDateError, setToDateError] = useState("");
  const [organization, setOrganization] = useState(null);
  const [selectedInventoryLocations, setSelectedInventoryLocations] = useState([]);
  const [inventorySearchTerm, setInventorySearchTerm] = useState("");
  const [usingInventoryManagement, setUsingInventoryManagement] = useState(false);

  const [accountModules, setAccountModules] = useState([]);

  const [loading, setLoading] = useState({
    campaign: false,
    members: false,
    locations: false,
    devices: false,
    relatedData: false,
  });

  const [formData, setFormData] = useState({
    name: "",
    focus: "",
    startDate: "",
    endDate: "",
    notes: "",
    active: false,
  });

  const [members, setMembers] = useState([]);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [memberSearch, setMemberSearch] = useState("");
  const [selectedMemberSearch, setSelectedMemberSearch] = useState("");
  const [memberSort, setMemberSort] = useState("asc");
  const [selectedMemberSort, setSelectedMemberSort] = useState("asc");

  const [locations, setLocations] = useState([]);
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [locationSearch, setLocationSearch] = useState("");
  const [selectedLocationSearch, setSelectedLocationSearch] = useState("");
  const [locationSort, setLocationSort] = useState("asc");
  const [selectedLocationSort, setSelectedLocationSort] = useState("asc");

  const [devices, setDevices] = useState([]);
  const [selectedDevices, setSelectedDevices] = useState([]);
  const [deviceSearch, setDeviceSearch] = useState("");
  const [selectedDeviceSearch, setSelectedDeviceSearch] = useState("");
  const [deviceSort, setDeviceSort] = useState("asc");
  const [selectedDeviceSort, setSelectedDeviceSort] = useState("asc");

  useEffect(() => {
    const item = location.state?.item;
    if (item) {
      setCampaignData(item);
      setCampaignId(item.id);

      const initialData = {
        name: item.name || "",
        focus: item.focus || "",
        startDate: item.startDate
          ? new Date(item.startDate).toLocaleDateString("en-CA")
          : "",
        endDate: item.endDate
          ? new Date(item.endDate).toLocaleDateString("en-CA")
          : "",
        notes: item.notes || "",
        active: item.active || false,
      };

      setFormData(initialData);
      setInitialFormData(initialData);

      if (item.customValues) {
        const parsedCustomValues =
          typeof item.customValues === "string"
            ? JSON.parse(item.customValues)
            : item.customValues;

        setCustomValuesInitialData(parsedCustomValues);

        if (customValuesRef.current) {
          customValuesRef.current.setModuleData(parsedCustomValues);
        }
      }
    } else if (accountId) {
      console.log("Fetching campaign data for account:", accountId);
      fetchCampaignData(accountId);
    }
  }, [location.state, accountId]);

  useEffect(() => {
    if (!campaignId) return;

    const fetchCampaignDetails = async () => {
      try {
        setLoading((prev) => ({ ...prev, campaign: true }));
        const response = await fetch(
          `${config.BASE_URL}/campaigns/${campaignId}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${authData.accessToken}`,
              "Content-Type": "application/json",
            },
          }
        );

        if (!response.ok) throw new Error("Failed to fetch campaign data");

        const data = await response.json();
        console.log("Fetched campaign details:", data);
        setCampaignData(data);

      
        let parsedInventoryLocations = data.selected_inventory_locations || [];
        if (typeof parsedInventoryLocations === 'string') {
          try {
            parsedInventoryLocations = JSON.parse(parsedInventoryLocations);
          } catch (error) {
            console.error("Error parsing selected_inventory_locations:", error);
            parsedInventoryLocations = [];
          }
        }

        console.log("Loaded campaign inventory data:", {
          raw: data.selected_inventory_locations,
          parsed: parsedInventoryLocations,
          type: typeof parsedInventoryLocations
        });


        setSelectedInventoryLocations(parsedInventoryLocations);
        setUsingInventoryManagement(data.usingInventoryManagement || false);

        const loaded = {
          name: data.name || "",
          focus: data.focus || "",
          startDate: data.startDate
            ? new Date(data.startDate).toLocaleDateString("en-CA")
            : "",
          endDate: data.endDate
            ? new Date(data.endDate).toLocaleDateString("en-CA")
            : "",
          notes: data.notes || "",
          active: data.active || false,
        };
        setFormData(loaded);
        setInitialFormData(loaded);
      } catch (error) {
        console.error("Error fetching campaign details:", error);
      } finally {
        setLoading((prev) => ({ ...prev, campaign: false }));
      }
    };

    fetchCampaignDetails();
  }, [campaignId, authData.accessToken]);

  useEffect(() => {
    if (!organizationId || !accountId) return;

    const fetchRelatedData = async () => {
      try {
        setLoading((prev) => ({ ...prev, relatedData: true }));

       
        const [
          membersResponse,
          locationsResponse,
          devicesResponse,
          associatedDevicesResponse,
        ] = await Promise.all([
          fetch(
            `${config.BASE_URL}/members?OrganizationId=${organizationId}`,
            {
              method: "GET",
              headers: {
                Authorization: `Bearer ${authData.accessToken}`,
                "Content-Type": "application/json",
              },
            }
          ),
          fetch(
            `${config.BASE_URL}/locations?accountId=${accountId}`,
            {
              method: "GET",
              headers: {
                Authorization: `Bearer ${authData.accessToken}`,
                "Content-Type": "application/json",
              },
            }
          ),
          fetch(
            `${config.BASE_URL}/devices/organization/${organizationId}`,
            {
              method: "GET",
              headers: {
                Authorization: `Bearer ${authData.accessToken}`,
                "Content-Type": "application/json",
              },
            }
          ),
          // For add mode, we don't need associated devices
          Promise.resolve({ json: () => [] }),
        ]);

        const [membersData, locationsData, devicesData] = await Promise.all([
          membersResponse.json(),
          locationsResponse.json(),
          devicesResponse.json(),
          associatedDevicesResponse.json(),
        ]);

        // Process data
        const sortedMembers = membersData.sort(
          (a, b) => new Date(a.createdAt) - new Date(b.createdAt)
        );


        if (!campaignData) {
          setMembers(sortedMembers);
          setLocations(locationsData);
          setDevices(devicesData);
          setSelectedMembers([]);
          setSelectedLocations([]);
          setSelectedDevices([]);
        } else {
  
          const selectedMembers = campaignData.campaignUsers
            ? campaignData.campaignUsers
              .map((cu) => {
                const member = membersData.find(
                  (member) => member.id === cu.UserId
                );
                return member ? { ...cu, ...member } : null;
              })
              .filter(Boolean)
            : [];

          const selectedDevices = campaignData.campaignDevices
            ? campaignData.campaignDevices
              .map((cd) => {
                const device = devicesData.find(
                  (device) => device.device.id === cd.DeviceId
                );
                return device ? { ...cd, ...device } : null;
              })
              .filter(Boolean)
            : [];

          const selectedLocations = campaignData.locationCampaigns
            ? campaignData.locationCampaigns
              .map((lc) => {
                const location = locationsData.find(
                  (location) => location.id === lc.LocationId
                );
                return location ? { ...lc, ...location } : null;
              })
              .filter(Boolean)
            : [];

          setSelectedMembers(selectedMembers);
          setSelectedDevices(selectedDevices);
          setSelectedLocations(selectedLocations);

          setMembers(
            sortedMembers.filter(
              (member) =>
                !selectedMembers.some((selected) => selected.id === member.id)
            )
          );

          setLocations(
            locationsData.filter(
              (location) =>
                !selectedLocations.some(
                  (selected) => selected.id === location.id
                )
            )
          );

          setDevices(
            devicesData.filter(
              (device) =>
                !selectedDevices.some((selected) => selected.id === device.id)
            )
          );
        }
      } catch (error) {
        console.error("Error fetching related data:", error);
      } finally {
        setLoading((prev) => ({ ...prev, relatedData: false }));
      }
    };

    fetchRelatedData();
  }, [campaignData, authData.accessToken, organizationId, accountId]);

  useEffect(() => {
    if (!organizationId) return;

    const fetchOrganization = async () => {
      try {
        const res = await fetch(
          `${config.BASE_URL}/organizations/${organizationId}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${authData.accessToken}`,
              "Content-Type": "application/json",
            },
          }
        );
        if (!res.ok) throw new Error("Failed to load organization");
        const data = await res.json();
        
      
        if (data.locationHierarchy && typeof data.locationHierarchy === 'string') {
          try {
            data.locationHierarchy = JSON.parse(data.locationHierarchy);
          } catch (error) {
            console.error("Error parsing locationHierarchy:", error);
            data.locationHierarchy = [];
          }
        }
        
        setOrganization(data);
        setUsingInventoryManagement(data.usingInventoryManagement || false);
      } catch (err) {
        console.error("Error fetching organization:", err);
      }
    };

    fetchOrganization();
  }, [organizationId, authData.accessToken]);

 
  useEffect(() => {
    if (organization && campaignData && selectedInventoryLocations.length > 0) {
  
      const needsConversion = selectedInventoryLocations.some(location => typeof location === 'string');
      
      if (needsConversion) {
        console.log("Converting inventory location IDs to objects:", selectedInventoryLocations);
        
        const inventoryLocationObjects = selectedInventoryLocations.map(locationId => {
          const findLocationInHierarchy = (locations, targetId) => {
            for (let location of locations) {
              if (location.id === targetId) {
                return location;
              }
              if (location.children && location.children.length > 0) {
                const found = findLocationInHierarchy(location.children, targetId);
                if (found) return found;
              }
            }
            return null;
          };
          
          return findLocationInHierarchy(organization.locationHierarchy || [], locationId);
        }).filter(Boolean);
        
        console.log("Converted inventory locations:", {
          original: selectedInventoryLocations,
          converted: inventoryLocationObjects
        });
        
        setSelectedInventoryLocations(inventoryLocationObjects);
      }
    }
  }, [organization, campaignData]);

  useEffect(() => {
    if (!accountId) return;

    const fetchCampaigns = async () => {
      try {
        setLoading((prev) => ({ ...prev, campaign: true }));
        const response = await fetch(
          `${config.BASE_URL}/campaigns/campaign/${accountId}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${authData.accessToken}`,
              "Content-Type": "application/json",
            },
          }
        );

        if (!response.ok) throw new Error("Failed to fetch campaigns");

        const data = await response.json();
        setCampaigns(Array.isArray(data) ? data : [data]);
      } catch (error) {
        console.error("Error fetching campaigns:", error);
        setCampaigns([]);
      } finally {
        setLoading((prev) => ({ ...prev, campaign: false }));
      }
    };

    fetchCampaigns();
  }, [accountId, authData.accessToken]);

  useEffect(() => {
    if (!accountId) return;

    const fetchAccount = async () => {
      try {
        const res = await fetch(
          `${config.BASE_URL}/accounts/${accountId}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${authData.accessToken}`,
              "Content-Type": "application/json",
            },
          }
        );
        if (!res.ok) throw new Error("Failed to load account");
        const data = await res.json();
        setAccountModules(data.modules || []);
        setAccountName(data.name || "");
      } catch (err) {
        console.error("Error fetching account modules:", err);
      }
    };

    fetchAccount();
  }, [accountId, authData.accessToken]);

  const fetchCampaignData = async (accountId) => {
    try {
      setLoading((prev) => ({ ...prev, campaign: true }));
      const response = await fetch(
        `${config.BASE_URL}/campaigns/campaign/${accountId}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${authData.accessToken}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) throw new Error("Failed to fetch campaign data");

      const data = await response.json();
      setCampaignData(data);
      setFormData({
        name: data.name || "",
        focus: data.focus || "",
        startDate: data.startDate || "",
        endDate: data.endDate || "",
        notes: data.notes || "",
        active: data.active || false,
      });

      if (data.customValues) {
        customValuesRef.current?.setModuleData(data.customValues);
      }
    } catch (error) {
      console.error("Error fetching campaign data:", error);
    } finally {
      setLoading((prev) => ({ ...prev, campaign: false }));
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    setFormData((prevData) => ({
      ...prevData,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleDateValidation = () => {
    const { startDate, endDate } = formData;

    if (endDate) {
      const today = new Date().toISOString().split("T")[0];

      if (endDate < today) {
        alert("To Date cannot be a past date.");
        setFormData((prev) => ({ ...prev, endDate: "" }));
        return;
      }

      if (startDate && endDate < startDate) {
        alert("To Date cannot be before From Date.");
        setFormData((prev) => ({ ...prev, endDate: "" }));
        return;
      }
    }
  };

  const checkCustomValuesChanges = () => {
    if (customValuesRef.current && customValuesInitialData) {
      const hasChanges = customValuesRef.current.hasChanges(
        customValuesInitialData
      );
      setCustomValuesChanged(hasChanges);
    }
  };

  const hasFormChanges = () => {
    if (!campaignId || !initialFormData) return true;
    return JSON.stringify(formData) !== JSON.stringify(initialFormData);
  };

  const hasRelatedChanges = () => {
    if (!campaignData) return false;

    const membersChanged =
      selectedMembers.length !== (campaignData.campaignUsers?.length || 0) ||
      !selectedMembers.every((member) =>
        campaignData.campaignUsers?.some((cu) => cu.UserId === member.id)
      );

    const locationsChanged =
      selectedLocations.length !==
      (campaignData.locationCampaigns?.length || 0) ||
      !selectedLocations.every((location) =>
        campaignData.locationCampaigns?.some(
          (lc) => lc.LocationId === location.id
        )
      );

    const devicesChanged =
      selectedDevices.length !== (campaignData.campaignDevices?.length || 0) ||
      !selectedDevices.every((device) =>
        campaignData.campaignDevices?.some(
          (cd) => cd.DeviceId === device.device.id
        )
      );


    let parsedCampaignInventoryLocations = campaignData.selected_inventory_locations || [];
    if (typeof parsedCampaignInventoryLocations === 'string') {
      try {
        parsedCampaignInventoryLocations = JSON.parse(parsedCampaignInventoryLocations);
      } catch (error) {
        console.error("Error parsing campaignData.selected_inventory_locations:", error);
        parsedCampaignInventoryLocations = [];
      }
    }

    const inventoryChanged =
      usingInventoryManagement !== (campaignData.usingInventoryManagement || false) ||
      JSON.stringify(selectedInventoryLocations) !== JSON.stringify(parsedCampaignInventoryLocations);

    return (
      membersChanged ||
      locationsChanged ||
      devicesChanged ||
      inventoryChanged
    );
  };

  const handleCustomValuesChange = () => {
    setCustomValuesChanged(true);
  };

  const isFormValid = () => {
    const hasName = formData.name.trim();

    if (campaignId) {
      const hasCustomValuesChanges =
        customValuesChanged ||
        (customValuesRef.current && customValuesRef.current.hasChanges());

      return (
        hasName &&
        (hasFormChanges() || hasRelatedChanges() || hasCustomValuesChanges)
      );
    } else {
      const hasTabData =
        selectedMembers.length > 0 ||
        selectedLocations.length > 0 ||
        selectedDevices.length > 0 ||
        selectedInventoryLocations.length > 0 ||
        (customValuesRef.current &&
          Object.keys(customValuesRef.current.getModuleData() || {}).length > 0);

      return hasName && hasTabData;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isFormValid() || isSubmitting) return;

    setIsSubmitting(true);
    console.log("Submitting form data:", formData);

    if (
      !customValuesRef.current ||
      typeof customValuesRef.current.getModuleData !== "function"
    ) {
      console.error("CustomValues ref not available.");
      alert("An error occurred: CustomValues module is not initialized.");
      setIsSubmitting(false);
      return;
    }

    const customValues = customValuesRef.current.getModuleData();

    const payload = {
      ...formData,
      active: formData.active,
      startDate: formData.startDate || null,
      endDate: formData.endDate || null,
      selected_members: selectedMembers,
      selected_devices: selectedDevices,
      selected_locations: selectedLocations,
      usingInventoryManagement: usingInventoryManagement,
      selected_inventory_locations: selectedInventoryLocations,
      customValues,
      AccountId: accountId,
      OrganizationId: organizationId,
    };

    console.log("Campaign payload being sent:", {
      usingInventoryManagement,
      selected_inventory_locations: selectedInventoryLocations,
      payloadKeys: Object.keys(payload)
    });

    const method = campaignId ? "PUT" : "POST";
    const url = campaignId
      ? `${config.BASE_URL}/campaigns/${campaignId}`
      : `${config.BASE_URL}/campaigns`;

    try {
      const response = await axios({
        method,
        url,
        data: payload,
        headers: {
          Authorization: `Bearer ${authData.accessToken}`,
          "Content-Type": "application/json",
        },
      });

      const action = campaignId ? "updated" : "saved";
      console.log(`Campaign ${action} successfully:`, response.data);

      setPopupMessage(`Campaign ${action} successfully!`);
      setPopupType("success");
      setShowPopup(true);

      setTimeout(() => {
        setShowPopup(false);
        navigate(`/organization/${organizationId}`, {
          state: {
            expandedAccountId: accountId,
            campaignMessage: `Campaign ${action} successfully!`,
          },
        });
      }, 4000);
    } catch (error) {
      console.error(
        "Error saving campaign:",
        error.response?.data || error.message
      );

      const isUpdate = !!campaignId;
      setPopupMessage(
        isUpdate ? "Error updating campaign!" : "Error saving campaign!"
      );
      setPopupType("error");
      setShowPopup(true);

      setTimeout(() => {
        setShowPopup(false);
        setIsSubmitting(false);
      }, 4000);
    }
  };

  const handleBackClick = (e) => {
    e.preventDefault();
    if (customValuesRef.current) {
      sessionStorage.setItem(
        "customValues",
        JSON.stringify(customValuesRef.current.getModuleData())
      );
    }
    navigate(-1);
  };

  const handleSearch = (e, type, isSelected = false) => {
    const value = e.target.value;
    switch (type) {
      case "Members":
        isSelected ? setSelectedMemberSearch(value) : setMemberSearch(value);
        break;
      case "Locations":
        isSelected
          ? setSelectedLocationSearch(value)
          : setLocationSearch(value);
        break;
      case "Devices":
        isSelected ? setSelectedDeviceSearch(value) : setDeviceSearch(value);
        break;
    }
  };

  const handleClearSearch = (type, isSelected = false) => {
    switch (type) {
      case "Members":
        isSelected ? setSelectedMemberSearch("") : setMemberSearch("");
        break;
      case "Locations":
        isSelected ? setSelectedLocationSearch("") : setLocationSearch("");
        break;
      case "Devices":
        isSelected ? setSelectedDeviceSearch("") : setDeviceSearch("");
        break;
    }
  };

  const handleSort = (type, isSelected = false) => {
    switch (type) {
      case "Members":
        isSelected
          ? setSelectedMemberSort((prev) => (prev === "asc" ? "desc" : "asc"))
          : setMemberSort((prev) => (prev === "asc" ? "desc" : "asc"));
        break;
      case "Locations":
        isSelected
          ? setSelectedLocationSort((prev) => (prev === "asc" ? "desc" : "asc"))
          : setLocationSort((prev) => (prev === "asc" ? "desc" : "asc"));
        break;
      case "Devices":
        isSelected
          ? setSelectedDeviceSort((prev) => (prev === "asc" ? "desc" : "asc"))
          : setDeviceSort((prev) => (prev === "asc" ? "desc" : "asc"));
        break;
    }
  };

  const filterAndSortItems = (itemList, search, sort, type) => {
    // Filter items based on search
    let filtered = itemList.filter((item) =>
      item.displayName.toLowerCase().includes(search.toLowerCase())
    );

    // For Devices, ensure uniqueness by id
    if (type === "Devices") {
      const uniqueMap = new Map();
      filtered.forEach((item) => {
        if (!uniqueMap.has(item.id)) {
          uniqueMap.set(item.id, item);
        }
      });
      filtered = Array.from(uniqueMap.values());
    }

    // Sort items
    return filtered.sort((a, b) => {
      if (sort === "asc") {
        return a.displayName.localeCompare(b.displayName);
      } else {
        return b.displayName.localeCompare(a.displayName);
      }
    });
  };

  const handleAddItem = (itemId, type) => {
    switch (type) {
      case "Members":
        const newSelectedMember = members.find(
          (member) => `${member.firstName} ${member.lastName}` === itemId
        );
        if (
          selectedMembers.some(
            (member) => `${member.firstName} ${member.lastName}` === itemId
          )
        ) {
          alert("Member already added.");
          return;
        }
        if (newSelectedMember) {
          setSelectedMembers((prev) => [...prev, newSelectedMember]);
          setMembers((prev) =>
            prev.filter(
              (member) => `${member.firstName} ${member.lastName}` !== itemId
            )
          );
        }
        break;

      case "Locations":
        const newSelectedLocation = locations.find(
          (location) => location.name === itemId
        );
        if (selectedLocations.some((location) => location.name === itemId)) {
          alert("Location already added.");
          return;
        }
        if (newSelectedLocation) {
          setSelectedLocations((prev) => [...prev, newSelectedLocation]);
          setLocations((prev) =>
            prev.filter((location) => location.name !== itemId)
          );
        }
        break;

      case "Devices":
        const newSelectedDevice = devices.find(
          (device) => device.device.id === itemId
        );
        if (selectedDevices.some((device) => device.device.id === itemId)) {
          alert("Device already added.");
          return;
        }
        if (newSelectedDevice) {
          setSelectedDevices((prev) => [...prev, newSelectedDevice]);
          setDevices((prev) =>
            prev.filter((device) => device.device.id !== itemId)
          );
        }
        break;

      default:
        break;
    }
  };

  const handleRemoveItem = (itemId, type) => {
    switch (type) {
      case "Members":
        const removedMember = selectedMembers.find(
          (member) => `${member.firstName} ${member.lastName}` === itemId
        );
        if (removedMember) {
          setMembers((prev) => [...prev, removedMember]);
          setSelectedMembers((prev) =>
            prev.filter(
              (member) => `${member.firstName} ${member.lastName}` !== itemId
            )
          );
        }
        break;
      case "Locations":
        const removedLocation = selectedLocations.find(
          (location) => location.name === itemId
        );
        if (removedLocation) {
          setLocations((prev) => [...prev, removedLocation]);
          setSelectedLocations((prev) =>
            prev.filter((location) => location.name !== itemId)
          );
        }
        break;
      case "Devices":
        const removedDevice = selectedDevices.find(
          (device) => device.device.id === itemId
        );
        if (removedDevice) {
          setDevices((prev) => [...prev, removedDevice]);
          setSelectedDevices((prev) =>
            prev.filter((device) => device.device.id !== itemId)
          );
        }
        break;
      default:
        break;
    }
  };

  const handleAssociateDeviceClick = () => {
    setShowModal(true);
  };

  const handleAddMember = () => {
    navigate(`/organization/${organizationId}/users/add`, {
      state: { orgId, orgName },
    });
  };

  const handleAddLocation = () => {
    navigate(`/organization/${organizationId}/locations/add`, {
      state: { accountId, organizationId },
    });
  };

  const getIcon = (type, className = "") => {
    switch (type) {
      case "Members":
        return <User className={className} size={35} />;
      case "Devices":
        return <Tablet className={className} size={35} />;
      case "Locations":
        return <Plane className={className} size={35} />;
      default:
        return null;
    }
  };

  const getLocationName = (locationId, locations) => {
    const findLocation = (items, targetId) => {
      for (let item of items) {
        if (item.id === targetId) {
          return item.name;
        }
        if (item.children && item.children.length > 0) {
          const found = findLocation(item.children, targetId);
          if (found) return found;
        }
      }
      return null;
    };
    return findLocation(locations, locationId) || `Location ${locationId}`;
  };

  const getLocationPath = (locationId, locations) => {
    const findPath = (items, targetId, currentPath = []) => {
      for (let item of items) {
        const newPath = [...currentPath, item.name];
        if (item.id === targetId) {
          return newPath;
        }
        if (item.children && item.children.length > 0) {
          const result = findPath(item.children, targetId, newPath);
          if (result) return result;
        }
      }
      return null;
    };
    return findPath(locations, locationId) || [`Location ${locationId}`];
  };

  const handleInventoryLocationSelection = (selectedIds) => {
    // Convert selected IDs to location objects with full hierarchy information
    const selectedLocationObjects = selectedIds.map(locationId => {
      const findLocationInHierarchy = (locations, targetId) => {
        for (let location of locations) {
          if (location.id === targetId) {
            return location;
          }
          if (location.children && location.children.length > 0) {
            const found = findLocationInHierarchy(location.children, targetId);
            if (found) return found;
          }
        }
        return null;
      };
      
      return findLocationInHierarchy(organization?.locationHierarchy || [], locationId);
    }).filter(Boolean); // Remove any null values
    
    console.log('Inventory location selection changed:', {
      selectedIds,
      selectedLocationObjects,
      previousSelection: selectedInventoryLocations
    });
    
    setSelectedInventoryLocations(selectedLocationObjects);
  };

  const handleInventorySearchChange = (searchTerm) => {
    setInventorySearchTerm(searchTerm);
  };

  const renderTabContent = (type) => {
    if (loading.relatedData) {
      return (
        <div className="campaign-loading-container">
          <Loader2 className="campaign-spinner" size={32} />
          <p>Loading {type.toLowerCase()} data...</p>
        </div>
      );
    }

    let items,
      selectedItems,
      itemSearch,
      selectedItemSearch,
      itemSort,
      selectedItemSort;

    switch (type) {
      case "Members":
        items = members.map((member) => ({
          id: `${member.firstName} ${member.lastName}`,
          displayName: `${member.firstName} ${member.lastName}`,
          role: member.organizationRole || "No Role",
        }));

        selectedItems = selectedMembers.map((member) => ({
          id: `${member.firstName} ${member.lastName}`,
          displayName: `${member.firstName} ${member.lastName}`,
          role: member.organizationRole || "No Role",
        }));

        itemSearch = memberSearch;
        selectedItemSearch = selectedMemberSearch;
        itemSort = memberSort;
        selectedItemSort = selectedMemberSort;
        break;

      case "Locations":
        items = locations.map((location) => ({
          id: location.name,
          displayName: location.name,
          country: location.country || "No Country",
        }));

        selectedItems = selectedLocations.map((location) => ({
          id: location.name,
          displayName: location.name,
          country: location.country || "No Country",
        }));

        itemSearch = locationSearch;
        selectedItemSearch = selectedLocationSearch;
        itemSort = locationSort;
        selectedItemSort = selectedLocationSort;
        break;

      case "Devices":

        items = devices.map((device) => ({
          id: device.device.id,
          displayName: device.device.deviceName || "Unknown",
        }));

        selectedItems = selectedDevices.map((device) => ({
          id: device.device.id,
          displayName: device.device.deviceName || "Unknown",
        }));

        itemSearch = deviceSearch;
        selectedItemSearch = selectedDeviceSearch;
        itemSort = deviceSort;
        selectedItemSort = selectedDeviceSort;
        break;

      default:
        return null;
    }

    const filteredItems = filterAndSortItems(items, itemSearch, itemSort, type);
    const filteredSelectedItems = filterAndSortItems(
      selectedItems,
      selectedItemSearch,
      selectedItemSort,
      type
    );

    const buttonText =
      type === "Devices"
        ? "Associate Device"
        : type === "Members"
          ? "Add Member"
          : "Add Location";

    return (
      <div className="campaign-devices-section">
        {showPopup && (
          <div className={`popup-${popupType}`}>{popupMessage}</div>
        )}

        {/* LEFT column - Selected items */}
        <div className="campaign-devices-column">
          <h3>{type}</h3>
          <div className="campaign-search-bar">
            <input
              type="text"
              placeholder="Search for..."
              value={selectedItemSearch}
              onChange={(e) => handleSearch(e, type, true)}
            />
            <Button
              color={selectedItemSearch ? "#006d7d" : "grey"}
              text="Clear"
              onClick={() => handleClearSearch(type, true)}
              className="campaign-clear-button"
            />
          </div>
          <div className="campaign-sort-by">
            <label>Sort By: </label>
            <select
              value={selectedItemSort}
              onChange={() => handleSort(type, true)}
            >
              <option value="asc">A → Z</option>
              <option value="desc">Z → A</option>
            </select>
          </div>
          <ul className="campaign-device-list">
            {filteredSelectedItems.map((item) => (
              <li
                key={item.id}
                onClick={() => handleRemoveItem(item.id, type)}
              >
                <span className="campaign-list-item-icon">
                  {getIcon(type)}
                </span>
                <span className="campaign-list-item-text">
                  <div>{item.displayName}</div>
                  <div
                    className="campaign-list-item-subtext"
                    style={{ fontSize: 12 }}
                  >
                    {type === "Members"
                      ? item.role
                      : type === "Locations"
                        ? item.country
                        : `ID: ${item.id}`}
                  </div>
                </span>
              </li>
            ))}
          </ul>
        </div>

        {/* RIGHT column - Available items */}
        <div className="campaign-devices-column">
          <h3>
            {`"${type === "Locations"
              ? accountName || "Selected"
              : organization?.name || "Selected"
              }" ${type}`}
          </h3>
          <div className="campaign-search-bar">
            <input
              type="text"
              placeholder="Search for..."
              value={itemSearch}
              onChange={(e) => handleSearch(e, type)}
            />
            <Button
              color="#006d7d"
              text="Clear"
              onClick={() => handleClearSearch(type)}
              className="campaign-clear-button"
            />
            <Button
              color="#006d7d"
              text={buttonText}
              onClick={() => {
                if (type === "Devices") {
                  handleAssociateDeviceClick();
                } else if (type === "Members") {
                  handleAddMember();
                } else if (type === "Locations") {
                  handleAddLocation();
                }
              }}
              className="campaign-action-button"
            />
          </div>
          <div className="campaign-sort-by">
            <label>Sort By: </label>
            <select value={itemSort} onChange={() => handleSort(type)}>
              <option value="asc">A → Z</option>
              <option value="desc">Z → A</option>
            </select>
          </div>
          <ul className="campaign-device-list">
            {filteredItems.map((item) => (
              <li key={item.id} onClick={() => handleAddItem(item.id, type)}>
                <span className="campaign-list-item-icon">{getIcon(type)}</span>
                <span className="campaign-list-item-text">
                  <div>{item.displayName}</div>
                  <div
                    className="campaign-list-item-subtext"
                    style={{ fontSize: 12 }}
                  >
                    {type === "Devices" ? `ID: ${item.id}` : type === "Members" ? item.role : item.country}
                  </div>
                </span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    );
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setModalSearch("");
    setSelectedDevice(null);
  };

  const handleDeviceSelection = (device) => {
    setSelectedDevice(device);
  };

  const handleSaveDevice = async () => {
    if (!selectedDevice) return;

    setLoading((prev) => ({ ...prev, devices: true }));

    try {
      await axios.put(
        `${config.BASE_URL}/devices/${selectedDevice.id}`,
        {
          OrganizationId: organizationId,
          device: JSON.stringify({
            deviceName: selectedDevice.device.deviceName,
            model: selectedDevice.device.model,
          }),
        },
        {
          headers: {
            Authorization: `Bearer ${authData.accessToken}`,
            "Content-Type": "application/json",
          },
        }
      );

      setDevices((prev) => [...prev, selectedDevice]);

      setShowModal(false);
      setSelectedDevice(null);
    } catch (error) {
      console.error("Error associating device:", error);
    } finally {
      setLoading((prev) => ({ ...prev, devices: false }));
    }
  };

  return (
    <div className="campaign-container">
      <div className="campaign-content-wrapper">
        <div className="campaign-header">
          <Button
            className="campaign-back-button"
            color="#006d7d"
            text="← Back"
            onClick={handleBackClick}
          />
          <h1>Campaign</h1>
        </div>

        {loading.campaign && !campaignData ? (
          <div className="campaign-loading-container">
            <Loader2 className="campaign-spinner" size={32} />
            <p>Loading campaign data...</p>
          </div>
        ) : (
          <>
            <div className="campaign-form-container">
              <form onSubmit={handleSubmit}>
                <div className="campaign-form-content">
                  <div className="campaign-form-left">
                    <div className="campaign-form-group">
                      <label htmlFor="name">Name *</label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    <div className="campaign-form-group">
                      <label htmlFor="fromDate">From Date</label>
                      <input
                        type="date"
                        id="startDate"
                        name="startDate"
                        value={formData.startDate}
                        onChange={handleInputChange}
                      />
                    </div>
                    <div className="campaign-form-group">
                      <label htmlFor="active">Active</label>
                      <div className="campaign-checkbox-wrapper">
                        <input
                          type="checkbox"
                          id="active"
                          name="active"
                          checked={formData.active}
                          onChange={handleInputChange}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="campaign-form-right">
                    <div className="campaign-form-group">
                      <label htmlFor="focus">Focus</label>
                      <input
                        type="text"
                        id="focus"
                        name="focus"
                        value={formData.focus}
                        onChange={handleInputChange}
                      />
                    </div>
                    <div className="campaign-form-group">
                      <label htmlFor="toDate">To Date</label>
                      <input
                        type="date"
                        id="endDate"
                        name="endDate"
                        value={formData.endDate}
                        onChange={(e) => {
                          setToDateError(""); // clear error when user types
                          handleInputChange(e);
                        }}
                        onBlur={() => {
                          const { startDate, endDate } = formData;
                          if (!endDate) return;

                          const today = new Date();
                          const end = new Date(endDate);
                          const start = startDate ? new Date(startDate) : null;

                          if (end < today.setHours(0, 0, 0, 0)) {
                            setToDateError("To Date cannot be a past date.");
                            setFormData((prev) => ({ ...prev, endDate: "" }));
                            return;
                          }

                          if (start && end < start) {
                            setToDateError(
                              "To Date cannot be before From Date."
                            );
                            setFormData((prev) => ({ ...prev, endDate: "" }));
                          }
                        }}
                        style={{
                          borderColor: toDateError ? "red" : undefined,
                        }}
                      />
                      {toDateError && (
                        <div
                          style={{
                            color: "red",
                            fontSize: "14px",
                            fontWeight: "500",
                            marginTop: "6px",
                          }}
                        >
                          {toDateError}
                        </div>
                      )}
                    </div>

                    <div className="campaign-form-group">
                      <label htmlFor="notes">Notes</label>
                      <textarea
                        id="notes"
                        name="notes"
                        value={formData.notes}
                        onChange={handleInputChange}
                      />
                    </div>
                  </div>
                </div>

                <div className="campaign-save-button-container">
                  <Button
                    type="submit"
                    color="#006d8b"
                    text={isSubmitting ? "Saving..." : "Save"}
                    className="campaign-save-button"
                    disabled={!isFormValid() || isSubmitting}
                    onClick={() => {
                      const form = document.querySelector("form");
                      const isUpdate = !!campaignId;

                      if (form && !form.checkValidity()) {
                        form.reportValidity();

                        setPopupMessage(
                          isUpdate
                            ? "Error updating campaign!"
                            : "Error saving campaign!"
                        );
                        setPopupType("error");
                        setShowPopup(true);
                        setTimeout(() => setShowPopup(false), 4000);
                      }
                    }}
                  />
                </div>
              </form>
            </div>

            <div className="campaign-tabs">
              <Button
                color={activeTab === "Members" ? "#006d8b" : "#878686"}
                text="Members"
                onClick={() => setActiveTab("Members")}
                className={`campaign-tab-button ${activeTab === "Members" ? "active" : ""
                  }`}
              />
              <Button
                color={activeTab === "Devices" ? "#006d8b" : "#878686"}
                text="Devices"
                onClick={() => setActiveTab("Devices")}
                className={`campaign-tab-button ${activeTab === "Devices" ? "active" : ""
                  }`}
              />
              <Button
                color={activeTab === "Locations" ? "#006d8b" : "#878686"}
                text="Locations"
                onClick={() => setActiveTab("Locations")}
                className={`campaign-tab-button ${activeTab === "Locations" ? "active" : ""
                  }`}
              />
              <Button
                color={activeTab === "CustomValues" ? "#006d8b" : "#878686"}
                text="Custom Values"
                onClick={() => setActiveTab("CustomValues")}
                className={`campaign-tab-button ${activeTab === "CustomValues" ? "active" : ""
                  }`}
              />
              {isInventoryAccessEnabled && (
                <Button
                  color={activeTab === "Inventory" ? "#006d8b" : "#878686"}
                  text="Inventory"
                  onClick={() => setActiveTab("Inventory")}
                  className={`campaign-tab-button ${activeTab === "Inventory" ? "active" : ""
                    }`}
                />
              )}

              {activeTab === "Members" && renderTabContent("Members")}
              {activeTab === "Devices" && renderTabContent("Devices")}
              {activeTab === "Locations" && renderTabContent("Locations")}
              <div
                style={{
                  display: activeTab === "CustomValues" ? "block" : "none",
                }}
              >
                <CustomValues
                  ref={customValuesRef}
                  campaignData={campaignData}
                  onChange={handleCustomValuesChange}
                  onLogoChange={setLogoUrl}
                  accountModules={accountModules}
                  canAccessInventory={organization?.canAccessInventory}
                />
              </div>
              {isInventoryAccessEnabled && activeTab === "Inventory" && (
                <div className="campaign-inventory-section">
                  {/* Toggle at the start */}
                  <div className="org-toggle-container">
                    <label className="org-toggle-label">Using Inventory Management</label>
                    <label className="org-toggle-switch">
                      <input
                        type="checkbox"
                        name="usingInventoryManagement"
                        checked={usingInventoryManagement}
                        onChange={(e) => {
                          setUsingInventoryManagement(e.target.checked);
                        }}
                      />
                      <span className="org-toggle-slider"></span>
                    </label>
                  </div>
                  
                  {organization?.locationHierarchy && organization.locationHierarchy.length > 0 ? (
                    <div className="inventory-content">
                      {/* Search Bar */}
                      <div className="inventory-search-container">
                        <div className="search-input-wrapper">
                          <span className="search-icon">🔍</span>
                          <input
                            type="text"
                            placeholder="Search locations..."
                            value={inventorySearchTerm}
                            onChange={(e) => handleInventorySearchChange(e.target.value)}
                            className="inventory-search-input"
                          />
                        </div>
                      </div>

                      {/* Selected Locations Display */}
                      {selectedInventoryLocations.length > 0 && (
                        <div className="selected-locations-container">
                          <h4>Selected ({selectedInventoryLocations.length}):</h4>
                          <div className="selected-locations-list">
                            {selectedInventoryLocations.map((location, index) => {
                              const path = getLocationPath(location.id, organization.locationHierarchy);
                              return (
                                <div key={location.id || index} className="selected-location-item">
                                  {path.join(' > ')}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                      
                      {/* Location Hierarchy Selector */}
                      <div className="location-selector-container">
                        <CampaignLocation
                          locationHierarchy={organization.locationHierarchy}
                          selectedLocations={selectedInventoryLocations.map(location => location.id)}
                          onSelectionChange={handleInventoryLocationSelection}
                          searchTerm={inventorySearchTerm}
                          onSearchChange={handleInventorySearchChange}
                        />
                      </div>
                    </div>
                  ) : (
                    <div className="no-hierarchy-message">
                      <p>No location hierarchy configured for this organization.</p>
                      <p>Please configure the location hierarchy in the organization settings to use inventory management.</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </>
        )}

        {showModal && (
          <DeviceModal
            showModal={showModal}
            handleCloseModal={handleCloseModal}
            modalSearch={modalSearch}
            setModalSearch={setModalSearch}
            sortedDevices={devices}
            selectedDevice={selectedDevice}
            handleDeviceSelection={handleDeviceSelection}
            handleSave={handleSaveDevice}
            loading={loading.devices}
          />
        )}
      </div>
    </div>
  );
};

export default Campaign;
